import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';
import '../css/parvekshak.css';

const PER_PAGE = 20;

function parseSnapshotName(fname) {
  const base = fname.replace(/\.(jpg|jpeg|png)$/i, '');
  const m    = base.match(/^snapshot_cam(\d+)_(\d{8})_(\d{6})$/);
  if (m) {
    const [, cam, date, time] = m;
    const y = date.slice(0,4), mo = date.slice(4,6), d = date.slice(6,8);
    const h = time.slice(0,2), mi = time.slice(2,4), s = time.slice(4,6);
    return { cam: +cam, date: `${d}/${mo}/${y}`, time: `${h}:${mi}:${s}` };
  }
  return { cam: null, date: '', time: '' };
}

function AuthImg({ path, alt, className, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    apiFetch(path)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="alert-card-thumb-placeholder">📷</div>;
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
}

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`pk-toast ${type}`}>{msg}</div>;
}

export default function SnapshotsPage() {
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [lbSrc,    setLbSrc]    = useState(null);
  const [toast,    setToast]    = useState(null);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/ui/alerts?page=${page}&per_page=${PER_PAGE}&only=snapshot`);
      const d = await r.json();
      setItems(d.alerts || []);
      setTotal(d.total  || 0);
    } catch {
      showToast('Failed to load snapshots', 'err');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!lightbox) { setLbSrc(null); return; }
    let url;
    apiFetch(lightbox.path)
      .then(r => r.ok ? r.blob() : null)
      .then(blob => { if (blob) { url = URL.createObjectURL(blob); setLbSrc(url); } })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [lightbox]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="pk-page">
      <div className="page-toolbar">
        <span className="tb-label">{total} snapshots</span>
        <button className="tb-ghost-btn" onClick={load}>↺ Refresh</button>
      </div>

      {loading ? (
        <div className="pk-loading">⏳ Loading…</div>
      ) : items.length === 0 ? (
        <div className="pk-empty">
          <div className="pk-empty-icon">📷</div>
          <h3>No snapshots</h3>
          <p>Take a snapshot from the Cameras page{/* or Telegram bot — Telegram hidden from the UI */}.</p>
        </div>
      ) : (
        <div className="pk-grid">
          {items.map(snap => {
            const meta = parseSnapshotName(snap.filename);
            return (
              <div key={snap.filename} className="alert-card">
                <AuthImg
                  path={`/alerts/${encodeURIComponent(snap.filename)}/image`}
                  alt={snap.filename}
                  className="alert-card-thumb"
                  onClick={() => setLightbox({ path: `/alerts/${encodeURIComponent(snap.filename)}/image`, meta })}
                />
                <div className="alert-card-body">
                  <div className="alert-card-badges">
                    <span className="badge badge-instant">📸 Snapshot</span>
                    {meta.cam !== null && <span className="badge badge-cam">Cam {meta.cam + 1}</span>}
                  </div>
                  <p className="alert-card-time">{meta.date} {meta.time}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="pk-pagination">
          <button className="pk-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span className="pk-page-info">Page {page + 1} / {totalPages}</span>
          <button className="pk-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}

      {lightbox && (
        <div className="pk-lightbox" onClick={() => setLightbox(null)}>
          <button className="pk-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
          {lbSrc
            ? <img src={lbSrc} alt="snapshot" className="pk-lightbox-img" onClick={e => e.stopPropagation()} />
            : <div style={{ color:'#666', fontSize:14 }}>Loading…</div>
          }
          {lightbox.meta && (
            <div className="pk-lightbox-meta" onClick={e => e.stopPropagation()}>
              <span className="badge badge-instant">📸 Snapshot</span>
              {lightbox.meta.cam !== null && <span className="badge badge-cam">Cam {lightbox.meta.cam + 1}</span>}
              <span style={{ fontSize:12, color:'#aaa' }}>{lightbox.meta.date} {lightbox.meta.time}</span>
            </div>
          )}
        </div>
      )}

      <Toast msg={toast?.msg} type={toast?.type} />
    </div>
  );
}
